// src/pages/FileManagement.jsx
import React, { useState, useEffect, useRef } from "react";
import PageLayout from "../../components/pageLayout/PageLayout";
import CommonModal from "../modals/CommonModal";
import { API_URL } from "../../config/server";

function FileManagement() {
    const [isVisible, setIsVisible] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [fileId, setFileId] = useState(null);
    const [measureDate, setMeasureDate] = useState(null);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);

    const [modalState, setModalState] = useState({
        isOpen: false,
        type: "alarm",
        title: "",
        message: "",
        onConfirm: null,
    });

    const [searchForm, setSearchForm] = useState({
        schoolKeyword: "",
        schoolCode: "",
        schoolName: "",
        birthDate: "",
        measureDate: "",
        tagNumber: "",
        agilityExercise: "longJump",
        muscleExercise: "gripStrength",
    });
    const [schoolResults, setSchoolResults] = useState([]);
    const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
    const [measureDates, setMeasureDates] = useState([]);
    const [isSearchingSchool, setIsSearchingSchool] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const [highlightedDateIndex, setHighlightedDateIndex] = useState(-1);
    const searchTimeoutRef = useRef(null);
    const dropdownRef = useRef(null);
    const dropdownListRef = useRef(null);
    const dateDropdownRef = useRef(null);
    const dateDropdownListRef = useRef(null);

    const showAlertModal = (title, message) => {
        setModalState({
            isOpen: true,
            type: "alarm",
            title,
            message,
            onConfirm: null,
        });
    };
    const closeModal = () =>
        setModalState((prev) => ({ ...prev, isOpen: false }));

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setShowSchoolDropdown(false);
            if (
                dateDropdownRef.current &&
                !dateDropdownRef.current.contains(e.target)
            )
                setShowDateDropdown(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSchoolKeywordChange = (keyword) => {
        setSearchForm((prev) => ({
            ...prev,
            schoolKeyword: keyword,
            schoolCode: "",
            schoolName: "",
            measureDate: "",
        }));
        setMeasureDates([]);
        setHighlightedIndex(-1);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (keyword.length < 1) {
            setSchoolResults([]);
            setShowSchoolDropdown(false);
            return;
        }
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearchingSchool(true);
            try {
                const response = await fetch(
                    `${API_URL}/api/search/schools?keyword=${encodeURIComponent(keyword)}`,
                );
                if (response.ok) {
                    const data = await response.json();
                    setSchoolResults(data.schools || []);
                    setShowSchoolDropdown(true);
                }
            } catch (error) {
                console.error("학교 검색 실패:", error);
            } finally {
                setIsSearchingSchool(false);
            }
        }, 300);
    };

    const handleSchoolKeyDown = (e) => {
        if (!showSchoolDropdown || schoolResults.length === 0) return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev < schoolResults.length - 1 ? prev + 1 : 0;
                    setTimeout(() => {
                        dropdownListRef.current?.children[next]?.scrollIntoView(
                            { block: "nearest" },
                        );
                    }, 0);
                    return next;
                });
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev > 0 ? prev - 1 : schoolResults.length - 1;
                    setTimeout(() => {
                        dropdownListRef.current?.children[next]?.scrollIntoView(
                            { block: "nearest" },
                        );
                    }, 0);
                    return next;
                });
                break;
            case "Enter":
                e.preventDefault();
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < schoolResults.length
                )
                    handleSchoolSelect(schoolResults[highlightedIndex]);
                break;
            case "Escape":
                e.preventDefault();
                setShowSchoolDropdown(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSchoolSelect = async (school) => {
        setSearchForm((prev) => ({
            ...prev,
            schoolKeyword: school.school_name,
            schoolCode: school.school_code,
            schoolName: school.school_name,
            measureDate: "",
        }));
        setShowSchoolDropdown(false);
        if (showDownloadModal) {
            await loadUploadFiles(school.school_code);
            return;
        }
        try {
            const response = await fetch(
                `${API_URL}/api/search/measure-dates/${school.school_code}`,
            );
            if (response.ok) {
                const data = await response.json();
                setMeasureDates(data.dates || []);
            }
        } catch (error) {
            console.error("측정일 조회 실패:", error);
        }
    };

    const validateFile = (file) => {
        if (!file)
            return { isValid: false, message: "파일이 선택되지 않았습니다." };
        const ext = file.name
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();
        if (![".xlsx", ".xls", ".csv"].includes(ext))
            return {
                isValid: false,
                message: `지원하지 않는 파일 형식입니다.\n엑셀 파일(.xlsx, .xls, .csv)만 업로드 가능합니다.\n\n선택한 파일: ${file.name}`,
            };
        const mimes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
        ];
        if (file.type && !mimes.includes(file.type))
            return {
                isValid: false,
                message: `지원하지 않는 파일 형식입니다.\n엑셀 파일(.xlsx, .xls, .csv)만 업로드 가능합니다.\n\n선택한 파일: ${file.name}`,
            };
        if (file.size > 50 * 1024 * 1024)
            return {
                isValid: false,
                message: `파일 크기가 너무 큽니다.\n최대 50MB까지 업로드 가능합니다.\n\n선택한 파일 크기: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
            };
        return { isValid: true, message: "" };
    };

    const uploadFile = async (file) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await fetch(`${API_URL}/api/upload/excel`, {
                method: "POST",
                body: formData,
            });
            if (response.ok) {
                const result = await response.json();
                setFileId(result.file_id);
                setMeasureDate(result.measure_date);
                showAlertModal("업로드 완료", "파일 업로드 성공!");
                return true;
            } else {
                const error = await response.json();
                setUploadedFile(null);
                showAlertModal(
                    "업로드 실패",
                    "파일 업로드 실패: " + (error.message || "오류 발생"),
                );
                return false;
            }
        } catch {
            setUploadedFile(null);
            showAlertModal("연결 오류", "서버 연결에 실패했습니다.");
            return false;
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const v = validateFile(file);
        if (!v.isValid) {
            showAlertModal("파일 형식 오류", v.message);
            e.target.value = "";
            return;
        }
        setUploadedFile(file);
        await uploadFile(file);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };
    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (!file) return;
        const v = validateFile(file);
        if (!v.isValid) {
            showAlertModal("파일 형식 오류", v.message);
            return;
        }
        setUploadedFile(file);
        await uploadFile(file);
    };

    const loadUploadFiles = async (code) => {
        setIsLoadingFiles(true);
        setUploadFiles([]);
        try {
            const response = await fetch(
                `${API_URL}/api/upload/files/${code}`,
            );
            if (response.ok) {
                const data = await response.json();
                setUploadFiles(data.files || []);
            }
        } catch (error) {
            console.error("업로드 파일 목록 조회 실패:", error);
        } finally {
            setIsLoadingFiles(false);
        }
    };

    const openDownloadModal = () => {
        setSearchForm((prev) => ({
            ...prev,
            schoolKeyword: "",
            schoolCode: "",
            schoolName: "",
        }));
        setSchoolResults([]);
        setUploadFiles([]);
        setShowDownloadModal(true);
    };

    // 업로드 직후면 바로 다운로드, 새로고침 등으로 정보가 없으면 목록에서 선택
    const handleDownload = () => {
        if (fileId) {
            downloadExcel(fileId, uploadedFile?.name);
        } else {
            openDownloadModal();
        }
    };

    const downloadExcel = async (id, downloadName) => {
        setIsDownloading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/download/excel/${id}`,
                { method: "GET" },
            );
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = downloadName || "측정결과.xlsx";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setShowDownloadModal(false);
                showAlertModal(
                    "다운로드 완료",
                    "파일 다운로드가 완료되었습니다.",
                );
            } else {
                const error = await response.json();
                showAlertModal(
                    "다운로드 실패",
                    "다운로드 실패: " + (error.detail || "오류 발생"),
                );
            }
        } catch {
            showAlertModal("연결 오류", "서버 연결에 실패했습니다.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrintClick = () => {
        setSearchForm({
            schoolKeyword: "",
            schoolCode: "",
            schoolName: "",
            birthDate: "",
            measureDate: "",
            tagNumber: "",
            agilityExercise: "longJump",
            muscleExercise: "gripStrength",
        });
        setSchoolResults([]);
        setMeasureDates([]);
        setShowSearchModal(true);
    };

    const handleSearchAndPrint = async () => {
        if (!searchForm.schoolCode) {
            showAlertModal("입력 필요", "학교를 선택해주세요.");
            return;
        }
        if (!searchForm.measureDate) {
            showAlertModal("입력 필요", "측정일을 선택해주세요.");
            return;
        }
        if (!searchForm.tagNumber) {
            showAlertModal("입력 필요", "태그번호를 입력해주세요.");
            return;
        }
        if (!/^\d{6}$/.test(searchForm.birthDate)) {
            showAlertModal(
                "입력 필요",
                "생년월일 6자리를 입력해주세요. (예: 080315)",
            );
            return;
        }
        setIsPrinting(true);
        try {
            const response = await fetch(`${API_URL}/api/pdf`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    measure_date: searchForm.measureDate,
                    school_code: searchForm.schoolCode,
                    tag_number: searchForm.tagNumber,
                    birth_date: searchForm.birthDate,
                    agility_exercise: searchForm.agilityExercise,
                    muscle_exercise: searchForm.muscleExercise,
                }),
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `태그${searchForm.tagNumber}_PAPS결과지.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                showAlertModal(
                    "PDF 생성 완료",
                    "PDF 다운로드가 완료되었습니다.",
                );
            } else {
                const error = await response.json();
                showAlertModal(
                    "PDF 생성 실패",
                    error.detail || "학생을 찾을 수 없습니다.",
                );
            }
        } catch {
            showAlertModal("연결 오류", "서버 연결에 실패했습니다.");
        } finally {
            setIsPrinting(false);
        }
        setShowSearchModal(false);
    };

    // ★ 대표종목 선택 라디오 버튼 컴포넌트
    const ExerciseSelector = ({ label, name, value, onChange, options }) => (
        <div className="flex flex-col gap-2 sm:gap-3 md:gap-2">
            <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                {label}
            </label>
            <div className="flex gap-3">
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        className={`flex-1 py-3 sm:py-3.5 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl text-sm sm:text-base md:text-sm font-semibold transition-all duration-300 border-2 ${
                            value === option.value
                                ? "bg-purple-500/30 border-purple-400 text-purple-200 shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                                : "bg-slate-800/40 border-slate-600/30 text-slate-400 hover:border-purple-500/50 hover:text-slate-300"
                        }`}
                        onClick={() => onChange(option.value)}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <div
                                className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center ${
                                    value === option.value
                                        ? "border-purple-400"
                                        : "border-slate-500"
                                }`}
                            >
                                {value === option.value && (
                                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-purple-400"></div>
                                )}
                            </div>
                            {option.label}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );

    // 학교 검색 입력 (프린트 / 다운로드 모달 공용)
    // inline: 드롭다운을 겹쳐 띄우지 않고 흐름 안에 배치 (모달 밖으로 넘치지 않게)
    const renderSchoolSearch = ({ inline = false } = {}) => (
        <div
            className="flex flex-col gap-2 sm:gap-3 md:gap-2 relative"
            ref={dropdownRef}
        >
            <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                학교명
            </label>
            <div className="relative">
                <input
                    type="text"
                    placeholder="학교명을 입력하세요"
                    className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/60 border-2 border-purple-500/30 text-sm sm:text-base md:text-sm text-white placeholder:text-slate-500 focus:border-purple-400 focus:outline-none transition-all duration-300"
                    value={searchForm.schoolKeyword}
                    onChange={(e) =>
                        handleSchoolKeywordChange(
                            e.target.value,
                        )
                    }
                    onKeyDown={handleSchoolKeyDown}
                />
                {isSearchingSchool && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg
                            className="w-5 h-5 md:w-5 md:h-5 text-purple-400 animate-spin"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                    </div>
                )}
                {searchForm.schoolCode && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg
                            className="w-5 h-5 md:w-5 md:h-5 text-emerald-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                )}
            </div>

            {showSchoolDropdown &&
                schoolResults.length > 0 && (
                    <div
                        ref={dropdownListRef}
                        className={`${inline ? "" : "absolute top-full left-0 right-0 z-50 "}mt-1 bg-slate-800/95 backdrop-blur-xl border-2 border-purple-500/50 rounded-xl max-h-[min(200px,30vh)] md:max-h-[min(240px,35vh)] overflow-y-auto shadow-[0_0_30px_rgba(168,85,247,0.4)]`}
                    >
                        {schoolResults.map(
                            (school, idx) => (
                                <button
                                    key={idx}
                                    className={`w-full px-4 py-3 md:px-4 md:py-2.5 text-left text-sm text-white transition-colors duration-200 border-b border-slate-700/50 last:border-b-0 ${idx === highlightedIndex ? "bg-purple-500/30" : "hover:bg-purple-500/20"}`}
                                    onClick={() =>
                                        handleSchoolSelect(
                                            school,
                                        )
                                    }
                                    onMouseEnter={() =>
                                        setHighlightedIndex(
                                            idx,
                                        )
                                    }
                                >
                                    {school.school_name}
                                </button>
                            ),
                        )}
                    </div>
                )}
            {showSchoolDropdown &&
                schoolResults.length === 0 &&
                searchForm.schoolKeyword.length >= 1 &&
                !isSearchingSchool && (
                    <div className={`${inline ? "" : "absolute top-full left-0 right-0 z-50 "}mt-1 bg-slate-800/95 backdrop-blur-xl border-2 border-purple-500/50 rounded-xl px-4 py-3 md:px-4 md:py-2.5 shadow-[0_0_30px_rgba(168,85,247,0.4)]`}>
                        <span className="text-xs sm:text-sm text-slate-400">
                            검색 결과가 없습니다
                        </span>
                    </div>
                )}
        </div>
    );

    return (
        <PageLayout>
            <CommonModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                type={modalState.type}
                title={modalState.title}
                message={modalState.message}
                onConfirm={modalState.onConfirm}
            />

            <div className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 relative z-10 gap-5 sm:gap-6 md:gap-7 py-4 sm:py-6">
                {/* ==================== 헤더 ==================== */}
                <div
                    className={`w-full max-w-6xl transition-all duration-1000 ease-in-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10"}`}
                >
                    <div className="border-2 border-cyan-400/60 rounded-2xl sm:rounded-3xl md:rounded-2xl px-6 sm:px-16 md:px-24 py-5 sm:py-8 md:py-9 bg-slate-900/40 backdrop-blur-xl shadow-[0_0_40px_rgba(34,211,238,0.5),inset_0_0_20px_rgba(34,211,238,0.05)] text-center">
                        <h1 className="text-3xl sm:text-5xl md:text-[2.75rem] font-bold text-blue-500 mb-1">
                            파일 관리
                        </h1>
                        <p className="text-sm sm:text-lg md:text-base text-blue-300 tracking-widest m-0">
                            FILE MANAGEMENT
                        </p>
                    </div>
                </div>

                {/* ==================== 메인 카드 ==================== */}
                <div
                    className={`w-full max-w-6xl transition-all duration-1000 ease-in-out delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                >
                    <div className="bg-slate-900/60 backdrop-blur-[20px] border-2 border-purple-500/50 rounded-3xl sm:rounded-[36px] md:rounded-2xl px-5 sm:px-10 md:px-14 py-8 sm:py-12 md:py-11 shadow-[0_0_40px_rgba(168,85,247,0.5),inset_0_0_40px_rgba(168,85,247,0.08)]">
                        {/* 업로드 영역 */}
                        <div className="mb-6 sm:mb-8 md:mb-9">
                            <div className="text-xl sm:text-2xl md:text-[1.4rem] font-bold text-purple-400 mb-4 sm:mb-6 md:mb-5 text-center">
                                엑셀 파일 업로드
                            </div>
                            <div
                                className={`border-3 sm:border-4 md:border-2 border-dashed rounded-2xl sm:rounded-3xl md:rounded-2xl p-6 sm:p-8 md:p-10 text-center transition-all duration-300 cursor-pointer ${
                                    isUploading
                                        ? "border-yellow-400 bg-yellow-400/10"
                                        : isDragOver
                                          ? "border-cyan-400 bg-cyan-400/10"
                                          : uploadedFile
                                            ? "border-emerald-400 bg-emerald-400/10"
                                            : "border-purple-500/40 bg-slate-800/40 hover:border-purple-400 hover:bg-purple-500/10"
                                }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() =>
                                    !isUploading &&
                                    document.getElementById("fileInput").click()
                                }
                            >
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    disabled={isUploading}
                                />

                                {isUploading ? (
                                    <div className="flex flex-col items-center gap-3 md:gap-3">
                                        <div className="w-14 h-14 sm:w-20 sm:h-20 md:w-[4.5rem] md:h-[4.5rem] rounded-full bg-yellow-500/20 flex items-center justify-center">
                                            <svg
                                                className="w-7 h-7 sm:w-10 sm:h-10 md:w-9 md:h-9 text-yellow-400 animate-spin"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                />
                                            </svg>
                                        </div>
                                        <div className="text-base sm:text-xl md:text-lg font-semibold text-yellow-400">
                                            업로드 중...
                                        </div>
                                    </div>
                                ) : uploadedFile ? (
                                    <div className="flex flex-col items-center gap-3 md:gap-3">
                                        <div className="w-14 h-14 sm:w-20 sm:h-20 md:w-[4.5rem] md:h-[4.5rem] rounded-full bg-emerald-500/20 flex items-center justify-center">
                                            <svg
                                                className="w-7 h-7 sm:w-10 sm:h-10 md:w-9 md:h-9 text-emerald-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </div>
                                        <div className="text-sm sm:text-lg md:text-base font-semibold text-emerald-400 break-all px-2">
                                            {uploadedFile.name}
                                        </div>
                                        <div className="text-xs sm:text-sm md:text-sm text-slate-400">
                                            클릭하여 다른 파일 선택
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-3 md:gap-3">
                                        <div className="w-14 h-14 sm:w-20 sm:h-20 md:w-[4.5rem] md:h-[4.5rem] rounded-full bg-purple-500/20 flex items-center justify-center">
                                            <svg
                                                className="w-7 h-7 sm:w-10 sm:h-10 md:w-9 md:h-9 text-purple-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                                />
                                            </svg>
                                        </div>
                                        <div className="text-sm sm:text-lg md:text-base font-semibold text-purple-300">
                                            파일을 드래그하거나 클릭하여 업로드
                                        </div>
                                        <div className="text-xs sm:text-sm md:text-sm text-slate-400">
                                            .xlsx, .xls, .csv 파일만 지원
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 구분선 */}
                        <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent mb-6 sm:mb-8 md:mb-9"></div>

                        {/* ==================== 버튼 영역 ==================== */}
                        <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 md:gap-6">
                            {/* 다운로드 버튼 */}
                            <button
                                className={`flex flex-row items-center gap-4 px-5 py-3.5 sm:flex-col sm:gap-4 sm:px-12 sm:py-8 md:flex-col md:gap-3 md:px-12 md:py-7 rounded-2xl sm:rounded-3xl md:rounded-2xl transition-all duration-300 ${
                                    !isDownloading
                                        ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-2 border-emerald-500/50 hover:border-emerald-400 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] cursor-pointer"
                                        : "bg-slate-800/40 border-2 border-slate-600/30 cursor-not-allowed opacity-50"
                                }`}
                                onClick={handleDownload}
                                disabled={isDownloading}
                            >
                                <div
                                    className={`w-10 h-10 sm:w-16 sm:h-16 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 ${!isDownloading ? "bg-emerald-500/30" : "bg-slate-700/30"}`}
                                >
                                    {isDownloading ? (
                                        <svg
                                            className="w-5 h-5 sm:w-8 sm:h-8 md:w-7 md:h-7 text-emerald-400 animate-spin"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            className={`w-5 h-5 sm:w-8 sm:h-8 md:w-7 md:h-7 ${!isDownloading ? "text-emerald-400" : "text-slate-500"}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex flex-col flex-1 text-center sm:items-center md:items-center">
                                    <span
                                        className={`text-sm sm:text-xl md:text-base font-bold ${!isDownloading ? "text-emerald-400" : "text-slate-500"}`}
                                    >
                                        {isDownloading
                                            ? "다운로드 중..."
                                            : "다운로드"}
                                    </span>
                                    <span
                                        className={`text-[10px] sm:text-sm md:text-sm ${!isDownloading ? "text-emerald-300/70" : "text-slate-600"}`}
                                    >
                                        {fileId
                                            ? "측정 데이터 엑셀 저장"
                                            : "업로드한 파일 선택 후 저장"}
                                    </span>
                                </div>
                            </button>

                            {/* 프린트 버튼 */}
                            <button
                                className="flex flex-row items-center gap-4 px-5 py-3.5 sm:flex-col sm:gap-4 sm:px-12 sm:py-8 md:flex-col md:gap-3 md:px-12 md:py-7 rounded-2xl sm:rounded-3xl md:rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-500/50 hover:border-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all duration-300 cursor-pointer"
                                onClick={handlePrintClick}
                            >
                                <div className="w-10 h-10 sm:w-16 sm:h-16 md:w-14 md:h-14 rounded-full bg-blue-500/30 flex items-center justify-center shrink-0">
                                    <svg
                                        className="w-5 h-5 sm:w-8 sm:h-8 md:w-7 md:h-7 text-blue-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                        />
                                    </svg>
                                </div>
                                <div className="flex flex-col flex-1 text-center sm:items-center md:items-center">
                                    <span className="text-sm sm:text-xl md:text-base font-bold text-blue-400">
                                        프린트
                                    </span>
                                    <span className="text-[10px] sm:text-sm md:text-sm text-blue-300/70">
                                        PDF 출력
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== 개인 검색 모달 ==================== */}
            {showSearchModal && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900/95 backdrop-blur-xl border-2 border-purple-500/50 rounded-t-3xl sm:rounded-[40px] md:rounded-2xl px-6 sm:px-12 md:px-10 py-8 sm:py-10 md:py-9 shadow-[0_0_60px_rgba(168,85,247,0.6)] max-w-[580px] w-full sm:mx-8 max-h-[90vh] overflow-y-auto">
                        <div className="text-2xl sm:text-3xl md:text-[1.4rem] font-bold text-purple-400 mb-5 sm:mb-8 md:mb-5 text-center">
                            개인 프린트
                        </div>
                        <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent mb-6 sm:mb-10 md:mb-6"></div>

                        <div className="flex flex-col gap-5 sm:gap-7 md:gap-5">
                            {renderSchoolSearch()}

                            {/* 측정일 선택 */}
                            <div
                                className="flex flex-col gap-2 sm:gap-3 md:gap-2 relative"
                                ref={dateDropdownRef}
                            >
                                <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                                    측정일
                                </label>
                                {measureDates.length > 0 ? (
                                    <>
                                        <button
                                            type="button"
                                            className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/60 border-2 border-purple-500/30 text-sm sm:text-base md:text-sm text-left focus:border-purple-400 focus:outline-none transition-all duration-300 cursor-pointer flex items-center justify-between"
                                            onClick={() => {
                                                setShowDateDropdown(
                                                    (prev) => !prev,
                                                );
                                                setHighlightedDateIndex(-1);
                                            }}
                                            onKeyDown={(e) => {
                                                if (!showDateDropdown) {
                                                    if (
                                                        e.key === "ArrowDown" ||
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault();
                                                        setShowDateDropdown(
                                                            true,
                                                        );
                                                        setHighlightedDateIndex(
                                                            0,
                                                        );
                                                    }
                                                    return;
                                                }
                                                switch (e.key) {
                                                    case "ArrowDown":
                                                        e.preventDefault();
                                                        setHighlightedDateIndex(
                                                            (prev) => {
                                                                const next =
                                                                    prev <
                                                                    measureDates.length -
                                                                        1
                                                                        ? prev +
                                                                          1
                                                                        : 0;
                                                                setTimeout(
                                                                    () => {
                                                                        dateDropdownListRef.current?.children[
                                                                            next
                                                                        ]?.scrollIntoView(
                                                                            {
                                                                                block: "nearest",
                                                                            },
                                                                        );
                                                                    },
                                                                    0,
                                                                );
                                                                return next;
                                                            },
                                                        );
                                                        break;
                                                    case "ArrowUp":
                                                        e.preventDefault();
                                                        setHighlightedDateIndex(
                                                            (prev) => {
                                                                const next =
                                                                    prev > 0
                                                                        ? prev -
                                                                          1
                                                                        : measureDates.length -
                                                                          1;
                                                                setTimeout(
                                                                    () => {
                                                                        dateDropdownListRef.current?.children[
                                                                            next
                                                                        ]?.scrollIntoView(
                                                                            {
                                                                                block: "nearest",
                                                                            },
                                                                        );
                                                                    },
                                                                    0,
                                                                );
                                                                return next;
                                                            },
                                                        );
                                                        break;
                                                    case "Enter":
                                                    case " ":
                                                        e.preventDefault();
                                                        if (
                                                            highlightedDateIndex >=
                                                                0 &&
                                                            highlightedDateIndex <
                                                                measureDates.length
                                                        ) {
                                                            setSearchForm(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    measureDate:
                                                                        measureDates[
                                                                            highlightedDateIndex
                                                                        ],
                                                                }),
                                                            );
                                                            setShowDateDropdown(
                                                                false,
                                                            );
                                                        }
                                                        break;
                                                    case "Escape":
                                                        e.preventDefault();
                                                        setShowDateDropdown(
                                                            false,
                                                        );
                                                        break;
                                                    default:
                                                        break;
                                                }
                                            }}
                                        >
                                            <span
                                                className={
                                                    searchForm.measureDate
                                                        ? "text-white"
                                                        : "text-slate-500"
                                                }
                                            >
                                                {searchForm.measureDate ||
                                                    "측정일을 선택하세요"}
                                            </span>
                                            <svg
                                                className={`w-5 h-5 text-purple-400 transition-transform duration-200 ${showDateDropdown ? "rotate-180" : ""}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </button>

                                        {showDateDropdown && (
                                            <div
                                                ref={dateDropdownListRef}
                                                className="absolute top-full left-0 right-0 mt-1 z-50 bg-slate-800/95 backdrop-blur-xl border-2 border-purple-500/50 rounded-xl max-h-[200px] md:max-h-[240px] overflow-y-auto shadow-[0_0_30px_rgba(168,85,247,0.4)]"
                                            >
                                                {measureDates.map(
                                                    (date, idx) => (
                                                        <button
                                                            key={idx}
                                                            className={`w-full px-4 py-3 md:px-4 md:py-2.5 text-left text-sm text-white transition-colors duration-200 border-b border-slate-700/50 last:border-b-0 ${idx === highlightedDateIndex ? "bg-purple-500/30" : searchForm.measureDate === date ? "bg-purple-500/15" : "hover:bg-purple-500/20"}`}
                                                            onClick={() => {
                                                                setSearchForm(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        measureDate:
                                                                            date,
                                                                    }),
                                                                );
                                                                setShowDateDropdown(
                                                                    false,
                                                                );
                                                            }}
                                                            onMouseEnter={() =>
                                                                setHighlightedDateIndex(
                                                                    idx,
                                                                )
                                                            }
                                                        >
                                                            <span className="flex items-center justify-between">
                                                                {date}
                                                                {searchForm.measureDate ===
                                                                    date && (
                                                                    <svg
                                                                        className="w-4 h-4 text-emerald-400"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M5 13l4 4L19 7"
                                                                        />
                                                                    </svg>
                                                                )}
                                                            </span>
                                                        </button>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/40 border-2 border-slate-600/30 text-xs sm:text-sm text-slate-500">
                                        {searchForm.schoolCode
                                            ? "측정 데이터가 없습니다"
                                            : "학교를 먼저 선택해주세요"}
                                    </div>
                                )}
                            </div>

                            {/* 태그번호 */}
                            <div className="flex flex-col gap-2 sm:gap-3 md:gap-2">
                                <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                                    태그번호
                                </label>
                                <input
                                    type="number"
                                    placeholder="태그번호 입력"
                                    min="1"
                                    className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/60 border-2 border-purple-500/30 text-sm sm:text-base md:text-sm text-white placeholder:text-slate-500 focus:border-purple-400 focus:outline-none transition-all duration-300"
                                    value={searchForm.tagNumber}
                                    onChange={(e) =>
                                        setSearchForm((prev) => ({
                                            ...prev,
                                            tagNumber: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            {/* 생년월일 (본인 확인용) */}
                            <div className="flex flex-col gap-2 sm:gap-3 md:gap-2">
                                <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                                    생년월일
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="생년월일 6자리 (예: 080315)"
                                    className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/60 border-2 border-purple-500/30 text-sm sm:text-base md:text-sm text-white placeholder:text-slate-500 focus:border-purple-400 focus:outline-none transition-all duration-300"
                                    value={searchForm.birthDate}
                                    onChange={(e) =>
                                        setSearchForm((prev) => ({
                                            ...prev,
                                            birthDate: e.target.value
                                                .replace(/\D/g, "")
                                                .slice(0, 6),
                                        }))
                                    }
                                />
                            </div>

                            {/* ★ 구분선 */}
                            <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent my-1"></div>

                            {/* ★ 대표종목 선택 */}
                            <div className="flex flex-col gap-1 sm:gap-2 md:gap-1">
                                <p className="text-xs sm:text-sm md:text-xs text-slate-400 text-center">
                                    * 종합 등급 산출을 위한 대표 측정항목을
                                    선택해주세요
                                </p>
                            </div>

                            {/* 순발력 대표종목 */}
                            <ExerciseSelector
                                label="순발력"
                                name="agilityExercise"
                                value={searchForm.agilityExercise}
                                onChange={(val) =>
                                    setSearchForm((prev) => ({
                                        ...prev,
                                        agilityExercise: val,
                                    }))
                                }
                                options={[
                                    {
                                        value: "longJump",
                                        label: "제자리멀리뛰기",
                                    },
                                    { value: "50mRun", label: "50m 달리기" },
                                ]}
                            />

                            {/* 근력·근지구력 대표종목 */}
                            <ExerciseSelector
                                label="근력 · 근지구력"
                                name="muscleExercise"
                                value={searchForm.muscleExercise}
                                onChange={(val) =>
                                    setSearchForm((prev) => ({
                                        ...prev,
                                        muscleExercise: val,
                                    }))
                                }
                                options={[
                                    {
                                        value: "gripStrength",
                                        label: "악력",
                                    },
                                    {
                                        value: "rollingUp",
                                        label: "윗몸말아올리기",
                                    },
                                ]}
                            />
                        </div>

                        {/* 모달 버튼 */}
                        <div className="flex gap-3 sm:gap-4 md:gap-3 mt-8 sm:mt-10 md:mt-7">
                            <button
                                className="flex-1 py-3.5 sm:py-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl text-sm sm:text-base md:text-sm font-semibold bg-slate-700/50 border border-slate-600/50 text-slate-300 hover:bg-slate-600/50 transition-all duration-300"
                                onClick={() => setShowSearchModal(false)}
                                disabled={isPrinting}
                            >
                                취소
                            </button>
                            <button
                                className={`flex-1 py-3.5 sm:py-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl text-sm sm:text-base md:text-sm font-semibold transition-all duration-300 ${isPrinting ? "bg-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:shadow-[0_0_30px_rgba(168,85,247,0.7)]"}`}
                                onClick={handleSearchAndPrint}
                                disabled={isPrinting}
                            >
                                {isPrinting ? "PDF 생성 중..." : "프린트"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ==================== 다운로드 파일 선택 모달 ==================== */}
            {showDownloadModal && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900/95 backdrop-blur-xl border-2 border-emerald-500/50 rounded-t-3xl sm:rounded-[40px] md:rounded-2xl px-6 sm:px-12 md:px-10 py-8 sm:py-10 md:py-9 shadow-[0_0_60px_rgba(16,185,129,0.5)] max-w-[640px] w-full sm:mx-8 max-h-[90vh] flex flex-col">
                        <div className="text-2xl sm:text-3xl md:text-[1.4rem] font-bold text-emerald-400 mb-5 sm:mb-8 md:mb-5 text-center">
                            파일 다운로드
                        </div>
                        <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent mb-6 sm:mb-10 md:mb-6"></div>

                        <div className="flex flex-col gap-5 sm:gap-7 md:gap-5 flex-1 min-h-0">
                            {renderSchoolSearch({ inline: true })}

                            {/* 업로드 파일 목록 (남은 높이 안에서만 스크롤) */}
                            <div className="flex flex-col gap-2 sm:gap-3 md:gap-2 flex-1 min-h-0">
                                <label className="text-sm sm:text-base md:text-sm font-semibold text-purple-300">
                                    업로드한 파일
                                </label>
                                {!searchForm.schoolCode ? (
                                    <div className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/40 border-2 border-slate-600/30 text-xs sm:text-sm text-slate-500">
                                        학교를 먼저 선택해주세요
                                    </div>
                                ) : isLoadingFiles ? (
                                    <div className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/40 border-2 border-slate-600/30 text-xs sm:text-sm text-slate-400">
                                        불러오는 중...
                                    </div>
                                ) : uploadFiles.length === 0 ? (
                                    <div className="w-full px-4 py-3.5 sm:px-5 sm:py-4 md:px-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl bg-slate-800/40 border-2 border-slate-600/30 text-xs sm:text-sm text-slate-500">
                                        업로드된 파일이 없습니다
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
                                        {uploadFiles.map((file) => (
                                            <button
                                                key={file.file_id}
                                                type="button"
                                                disabled={isDownloading}
                                                className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border-2 border-emerald-500/20 text-left hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() =>
                                                    downloadExcel(
                                                        file.file_id,
                                                        file.file_name_origin,
                                                    )
                                                }
                                            >
                                                <div className="text-sm sm:text-base md:text-sm font-semibold text-emerald-300 break-all">
                                                    {file.file_name_origin}
                                                </div>
                                                <div className="text-xs text-slate-400 mt-1">
                                                    {file.grade_year
                                                        ? `${file.grade_year}학년 ${file.class_number}반`
                                                        : "학년·반 정보 없음"}
                                                    {file.author
                                                        ? ` · ${file.author}`
                                                        : ""}
                                                    {file.measure_date
                                                        ? ` · 측정일 ${file.measure_date}`
                                                        : ""}
                                                </div>
                                                <div className="text-xs text-slate-500 mt-0.5">
                                                    업로드 {file.upload_dt}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 모달 버튼 */}
                        <div className="flex gap-3 sm:gap-4 md:gap-3 mt-8 sm:mt-10 md:mt-7 shrink-0">
                            <button
                                className="flex-1 py-3.5 sm:py-4 md:py-3 rounded-xl sm:rounded-2xl md:rounded-xl text-sm sm:text-base md:text-sm font-semibold bg-slate-700/50 border border-slate-600/50 text-slate-300 hover:bg-slate-600/50 transition-all duration-300"
                                onClick={() => setShowDownloadModal(false)}
                                disabled={isDownloading}
                            >
                                {isDownloading ? "다운로드 중..." : "닫기"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}

export default FileManagement;
